import React, { useState } from "react";
import { useFormContext, useWatch, Controller } from "react-hook-form";
import Select from "react-select";
import { selectStyles } from "../../static/styles";
import { IFormInput, OptionsProps } from "../../types/order";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { GiCancel } from "react-icons/gi";

interface ProcessFormProps {
  index: number;
  processOptions: OptionsProps[];
}

const ProcessForm = ({ index, processOptions }: ProcessFormProps) => {
  const {
    register,
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<IFormInput>();

  const [cameraError, setCameraError] = useState<string | null>(null);

  const photo = useWatch({ control, name: `processes.${index}.photo` });

  const handleCapturePhoto = async () => {
    setCameraError(null);
    try {
      const image = await Camera.getPhoto({
        quality: 20,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
      });
      setValue(`processes.${index}.photo`, image?.dataUrl || null);
    } catch (error) {
      setCameraError(
        "Camera permission is not granted. Please allow camera access."
      );
    }
  };

  const handleSelectPhoto = async () => {
    setCameraError(null);
    try {
      const image = await Camera.getPhoto({
        quality: 20,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,
      });
      setValue(`processes.${index}.photo`, image?.dataUrl || null);
    } catch (error) {
      setCameraError(
        "Photo gallery permission is not granted. Please allow access."
      );
    }
  };

  const handleRemovePhoto = () => {
    setValue(`processes.${index}.photo`, null);
  };

  const handleChangeProcess = (process_id: number) => {
    if (process_id) {
      setValue(`processes.${index}.process_id`, process_id);
      clearErrors(`processes.${index}.process_id`);
    }
  };

  const processErrors = (errors?.processes as any)?.[index];

  return (
    <div className="relative px-2 py-4 w-full mt-4 before:content[''] before:bg-gradient-to-r before:from-primary-1 before:to-primary-2 before:absolute before:top-0 before:left-0 before:w-full before:h-full before:rounded before:-z-[1]">
      <h3 className="text-lg font-bold text-white mb-3">
        Process - {index + 1}
      </h3>

      {/* Photo capture/select */}
      <div className="mb-4">
        {photo ? (
          <div className="relative border-4 border-white/30 rounded-sm w-full h-[200px] p-2">
            <GiCancel
              onClick={handleRemovePhoto}
              className="absolute z-10 shadow bg-white top-3 left-3 text-2xl text-primary-1 border-2 border-primary-1 p-1 rounded-full cursor-pointer"
            />
            <img
              className="w-full h-full object-contain"
              src={photo}
              alt=""
            />
          </div>
        ) : (
          <div className="flex gap-4 justify-center">
            <button
              className="shadow rounded-full text-lg text-white bg-white/20 font-bold px-5 py-2"
              type="button"
              onClick={handleCapturePhoto}
            >
              Capture
            </button>
            <button
              className="shadow rounded-full text-lg text-white bg-white/20 font-bold px-5 py-2"
              type="button"
              onClick={handleSelectPhoto}
            >
              Select
            </button>
          </div>
        )}
        {cameraError && (
          <span className="text-red-300 text-sm mt-1 block">{cameraError}</span>
        )}
      </div>

      {/* Process dropdown */}
      <div>
        <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
          <Controller
            {...register(`processes.${index}.process_id`, {
              required: true,
              min: 1,
            })}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                value={
                  processOptions.find(
                    (val) => val.value === field?.value
                  ) || null
                }
                className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2/70 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                styles={selectStyles}
                placeholder={"Enter process name"}
                options={processOptions}
                onChange={(val: any) => handleChangeProcess(val?.value)}
              />
            )}
          />
        </div>
        {processErrors?.process_id && (
          <span className="text-red-500">Please select a process name</span>
        )}
      </div>

      {/* Qty input */}
      <div className="mt-6">
        <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
          <input
            className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2/70 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
            type="number"
            pattern="[0-9]*"
            placeholder="Enter quantity"
            {...register(`processes.${index}.qty`, {
              required: true,
              min: 1,
            })}
            onFocus={(e) =>
              e.target.addEventListener(
                "wheel",
                function (e) {
                  e.preventDefault();
                },
                { passive: false }
              )
            }
          />
        </div>
        {processErrors?.qty && (
          <span className="text-red-500">Please enter quantity</span>
        )}
      </div>
    </div>
  );
};

export default ProcessForm;
